import { QueryHookOptions, QueryResult } from '@apollo/client/react/types/types';
import get from 'lodash/get';

export const PAGE_SIZE = 10;

// TODO: move to types
type PaginatedQueryVariables = {
  first?: number | null | undefined;
  last?: number | null | undefined;
  before?: string | null | undefined;
  after?: string | null | undefined;
  [key: string]: any;
};

export interface IPaginationProps {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  requestNextPage: () => void;
  requestPreviousPage: () => void;
}

type GetPaginationProps = (
  response: any,
  pageInfoPath: string,
  pageSize: number,
  paginationHandler?: PaginationHandler,
) => IPaginationProps;

export type PaginationHandler = (info: PaginationInfo) => void;

const getPaginationProps: GetPaginationProps = (
  response,
  pageInfoPath,
  pageSize,
  paginationHandler,
) => {
  const pageInfo = get(response.data, pageInfoPath, {});
  const { hasNextPage, hasPreviousPage, startCursor, endCursor } = pageInfo;

  const requestNextPage = () => {
    if (paginationHandler) {
      paginationHandler({
        first: pageSize,
        after: endCursor,
        last: null,
        before: null,
      });
    } else {
      response.refetch({
        first: pageSize,
        after: endCursor,
        last: null,
        before: null,
      });
    }
  };

  const requestPreviousPage = () => {
    if (paginationHandler) {
      paginationHandler({
        last: pageSize,
        before: startCursor,
        first: null,
        after: null,
      });
    } else {
      response.refetch({
        last: pageSize,
        before: startCursor,
        first: null,
        after: null,
      });
    }
  };

  return {
    hasNextPage: Boolean(hasNextPage),
    hasPreviousPage: Boolean(hasPreviousPage),
    requestNextPage,
    requestPreviousPage,
  };
};

export interface PaginationInfo {
  first?: number | null;
  last?: number | null;
  before?: string | null;
  after?: string | null;
}

export const usePaginatedQuery = function <
  TData,
  TVariables extends PaginatedQueryVariables = PaginatedQueryVariables,
>(
  useQuery: (
    baseOptions: QueryHookOptions<TData, TVariables> &
      ({ variables: TVariables; skip?: boolean } | { skip: boolean }),
  ) => QueryResult<TData, TVariables>,
  pageInfoPath: string,
  pageSize: number = PAGE_SIZE,
  baseOptions?: QueryHookOptions<TData, TVariables>,
  paginationInfo?: PaginationInfo,
  paginationHandler?: PaginationHandler,
) {
  const variables = {
    first: pageSize,
    ...baseOptions?.variables,
    last: null,
    before: null,
    after: null,
    ...paginationInfo,
  } as TVariables;
  const options = { ...baseOptions, variables };

  const response = useQuery(options);
  const paginationProps = getPaginationProps(response, pageInfoPath, pageSize, paginationHandler);

  return {
    ...response,
    paginationProps,
  };
};
